import { getAuth } from 'firebase/auth';
import { resolveBackendBaseUrl } from 'utils/backend-api';

export class QuizApiError extends Error {
    statusCode?: number;

    constructor(message: string, statusCode?: number) {
        super(message);
        this.name = 'QuizApiError';
        this.statusCode = statusCode;
    }
}

function normalizeErrorMessage(payload: unknown, fallbackMessage: string): string {
    if (!payload || typeof payload !== 'object') {
        return fallbackMessage;
    }

    const typedPayload = payload as Record<string, unknown>;
    const message = typedPayload.message;

    if (Array.isArray(message)) {
        return message.filter((item): item is string => typeof item === 'string').join('; ') || fallbackMessage;
    }

    if (typeof message === 'string' && message.trim().length > 0) {
        return message;
    }

    if (typeof typedPayload.error === 'string' && typedPayload.error.trim().length > 0) {
        return typedPayload.error;
    }

    return fallbackMessage;
}

export async function deleteQuizById(quizId: number): Promise<void> {
    const currentUser = getAuth().currentUser;

    if (!currentUser) {
        throw new QuizApiError('You must be signed in to delete a quiz.', 401);
    }

    const token = await currentUser.getIdToken();
    const baseUrl = resolveBackendBaseUrl();
    const endpoint = `${baseUrl}/quizzes/${quizId}`;

    const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (response.ok) {
        return;
    }

    const rawText = await response.text();
    let payload: unknown = null;

    if (rawText) {
        try {
            payload = JSON.parse(rawText);
        } catch {
            payload = { error: rawText };
        }
    }

    throw new QuizApiError(
        normalizeErrorMessage(payload, 'Failed to delete quiz.'),
        response.status,
    );
}